<template>
  <section class="page">
    <div class="page__header">
      <div class="page__title">
        <SharedBreadcrumbs :breadcrumbs />

        <FaqSearch />
      </div>

      <h1 class="typography__headline1">Список заказов и&nbsp;их данные</h1>
    </div>

    <div class="page__content">
      <FaqArticlesAside :menu />

      <FaqArticlesContent>
        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="spisok-zakazov"
            class="typography__headline2"
          >
            Список заказов
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>
              В&nbsp;меню слева перейдите в&nbsp;раздел &laquo;Заказы&raquo;. Если есть новые заказы&nbsp;&mdash;
              на&nbsp;этом разделе будет уведомление с&nbsp;их&nbsp;числом.
            </p>

            <p>Сверху справа можно добавить новый заказ.</p>

            <p>
              В&nbsp;списке можно быстро поменять статусы заказа и&nbsp;его оплаты, если он&nbsp;уже был обработан.
              Также там есть важная информация, возможность открыть чат с&nbsp;клиентом, если он&nbsp;уже есть, быстро
              перейти к&nbsp;редактированию или удалению.
            </p>

            <p>По&nbsp;нажатию в&nbsp;свободное от&nbsp;активных элементов место перейдем к&nbsp;странице заказа.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/spisok-zakazov-i-ikh-dannye-1.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>Над списком есть сортировка, фильтры и&nbsp;поисковая строка для быстрого поиска нужного заказа.</p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/spisok-zakazov-i-ikh-dannye-2.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="stranica-zakaza"
            class="typography__headline2"
          >
            Страница заказа
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>
              На&nbsp;открывшейся странице заказа сверху можно увидеть его номер. Под ним быстрый доступ
              к&nbsp;редактированию или удалению.
            </p>

            <p>
              Ниже можно быстро сменить статусы заявки и&nbsp;оплаты, добавить трек-номер или стоимость доставки
              и&nbsp;посмотреть всю нужную информацию.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/spisok-zakazov-i-ikh-dannye-3.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <div class="block-small">
            <p>Следующий блок показывает всю историю действий, совершенную с&nbsp;заказом.</p>

            <p>В конце страницы список товаров в заказе и их информация.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/spisok-zakazov-i-ikh-dannye-4.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>
            С&nbsp;добавлением заказов и&nbsp;их&nbsp;просмотром разобрались. Самое время перейти к&nbsp;изучению
            <BaseLink
              class="ui-link-secondary"
              to="/faq/categories/bot-i-integratsii"
              >бота и&nbsp;интеграции</BaseLink
            >.
          </p>
        </div>
      </FaqArticlesContent>
    </div>

    <div class="page__related">
      <h2
        class="typography__headline2"
        v-html="t('pages.faq.otherArticles')"
      />

      <FaqArticlesList :articles="relatedArticles" />
    </div>
  </section>

  <LazyFaqArticlesImageModal
    v-if="isModalOpen(MODAL.FaqArticlesImage) && activeImageSource"
    :src="activeImageSource"
    @close="() => (activeImageSource = null)"
  />
</template>

<script lang="ts" setup>
const { t } = useI18n();
const { isModalOpen } = useModalStore();
const activeImageSource = ref<null | string>(null);

const breadcrumbs = [
  {
    name: t("pages.faq.title"),
    url: "/faq",
  },
  {
    name: "Заказы с товарами",
    url: "/faq/categories/zakazy-s-tovarami",
  },
  {
    name: "Список заказов и&nbsp;их данные",
  },
];

const menu = [
  {
    name: "Список&nbsp;заказов",
    url: "spisok-zakazov",
  },
  {
    name: "Страница&nbsp;заказа",
    url: "stranica-zakaza",
  },
];

const relatedArticles = [
  {
    name: "Способы доставки",
    slug: "sposoby-dostavki",
  },
  {
    name: "Категории товаров",
    slug: "kategorii-tovarov",
  },
  {
    name: "Добавление и&nbsp;редактирование товара",
    slug: "dobavlenie-i-redaktirovanie-tovara",
  },
  {
    name: "Список товаров и&nbsp;их данные",
    slug: "spisok-tovarov-i-ikh-dannye",
  },
  {
    name: "Цифровые товары",
    slug: "tsifrovye-tovary",
  },
  {
    name: "Добавление и&nbsp;редактирование заказа",
    slug: "dobavlenie-i-redaktirovanie-zakaza",
  },
];

useSeoMeta({
  title: "Список заказов и их данные",
});
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__header {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__title {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}

.page__content {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

@media (--xl) {
  .page__content {
    flex-direction: row-reverse;
    justify-content: start;
    align-items: start;
  }
}

.page__related {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}
</style>
